import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Button,
  Typography,
  CircularProgress,
  IconButton
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { ToCurrencyString, ToPercentageStringWithDecimals } from 'services/formatService';
import {
  updateBidInPrism,
  appNotify
} from 'store/actions';
import { GetUnderwrite } from "api/revue/underwriteData";
import { sendClientLog } from 'services/logService';
import ClearIcon from '@material-ui/icons/Clear';
import classNames from 'classnames';

const SubmitBidModal = ({ classes, isModalOpen, onClose, exceedsMaxBidPrice, isBelowB2LFloor, user,
  selectedOpportunity, selectedValuation, bidPushedB2LFloor, updateBidInPrism, appNotify }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [latestSelectedOpportunity, setLatestSelectedOpportunity] = useState(selectedOpportunity);
  const [isHighestAndBest, setIsHighestAndBest] = useState(null);
  const [submitIsLoading, setSubmitIsLoading] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;
    setIsHighestAndBest(null);
    loadUnderwrite(selectedOpportunity.id);
  }, [isModalOpen]);

  const loadUnderwrite = (txnOppId) => {
    setIsLoading(true);
    GetUnderwrite(txnOppId).then(res => {
      if (!res) throw new Error('Failed to fetch latest transaction opportunity');
      setLatestSelectedOpportunity(res);
      setIsLoading(false);
    }).catch(error => {
      setLatestSelectedOpportunity(null);
      setIsLoading(false);
      appNotify(error.message, 'error');
    });
  }

  const handleCheckboxChange = (event) => {
    setIsHighestAndBest(event.target.checked);
  }

  const handleSubmitBid = () => {
    if (submitIsLoading) return;
    setSubmitIsLoading(true);
    updateBidInPrism(latestSelectedOpportunity.id, {
      valuationId: latestSelectedOpportunity.selectedValuationId,
      userId: user.id,
      highestAndBest: isHighestAndBest,
      overrideOrderValidation: (hasExistingBid && exceedsMaxBidPrice(latestSelectedOpportunity)),
      callback: handleBidSubmitted
    });
    sendClientLog('Info', `Property UW - User submitted bid for txnOpp ${selectedOpportunity.id}. Valuation ID: ${latestSelectedOpportunity.selectedValuationId}, Bid Price: ${latestSelectedOpportunity.selectedValuationBid}, HBP: ${isHighestAndBest}`);
  }

  const handleBidSubmitted = () => {
    setSubmitIsLoading(false);
    onClose();
  }

  const handleClose = (e, reason) => {
    if (reason === 'backdropClick') return; // updated handling for disableBackdropClick
    onClose();
  }

  const hasExistingBid = latestSelectedOpportunity?.hadSuccessfulPrismPush || latestSelectedOpportunity?.hadSuccessfulCrmPush;
  const bidPriceFormatted = latestSelectedOpportunity?.selectedValuationBid != null ? ToCurrencyString(latestSelectedOpportunity.selectedValuationBid) : '-';
  const maxBidPriceFormatted = latestSelectedOpportunity?.selectedValuationMaxBid != null ? ToCurrencyString(latestSelectedOpportunity.selectedValuationMaxBid) : '-';
  const hasNoBidReason = latestSelectedOpportunity?.selectedValuationNoBidReason !== null;
  //const hasExistingBidChanged = selectedOpportunity.hadSuccessfulPrismPush !== latestSelectedOpportunity?.hadSuccessfulPrismPush;
  const selectedValuationChanged = selectedValuation?.id !== latestSelectedOpportunity?.selectedValuationId;

  return (
    <Dialog onClose={handleClose} open={isModalOpen} maxWidth='sm' fullWidth>
      <DialogTitle>
        {hasNoBidReason ? (hasExistingBid ? 'Update No Bid' : 'Submit No Bid') : hasExistingBid ? 'Update Bid in CRM' : 'Submit Bid to CRM'}
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={onClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent style={{ paddingTop: 0 }}>
        {!latestSelectedOpportunity
          ? <div className={classes.flexContainer}>
            <Typography variant='subtitle2'>
              Failed to fetch latest transaction opportunity
              </Typography>
          </div>
          : isLoading
            ? <div className={classes.flexContainer}>
              <CircularProgress color='primary' />
            </div>
            :
            hasNoBidReason
              ? <>
                <DialogContentText id="alert-dialog-description">
                  Please confirm the No Bid information below.
                    </DialogContentText>
                <div className={classes.flexContainer}>
                  <div className={classes.flexGrow}>
                    <Typography variant='subtitle2' >
                      {'Valuation Type: ' + selectedValuation?.type}
                    </Typography>
                    <Typography variant='subtitle2' >
                      {'Underwrite User: ' + latestSelectedOpportunity.selectedValuationUserName}
                    </Typography>
                    <Typography variant='subtitle2' >
                      {'No Bid Reason: ' + latestSelectedOpportunity.selectedValuationNoBidReason}
                    </Typography>
                    {
                      latestSelectedOpportunity.selectedValuationUnderwriteNotes &&
                      <Typography variant='subtitle2' >
                        {'Underwrite Notes: ' + latestSelectedOpportunity.selectedValuationUnderwriteNotes}
                      </Typography>
                    }
                  </div>
                </div>
              </>
              : <>
                <DialogContentText id="alert-dialog-description">
                  Please confirm the Bid information below.
                  </DialogContentText>
                {selectedValuationChanged &&
                  <div className={classes.warningContainer}>
                    <WarningIcon className={classes.warningIcon} />
                    <Typography className={classes.warningText} variant='caption'>
                      The selected UW is no longer the latest. You will be submitting the new bid price shown below.
                      </Typography>
                  </div>
                }
                {exceedsMaxBidPrice(latestSelectedOpportunity) &&
                  <div className={classes.warningContainer}>
                    <WarningIcon className={classes.warningIcon} />
                    <Typography className={classes.warningText} variant='caption'>
                      The bid price exceeds the max bid price.
                      </Typography>
                  </div>
                }
                {isBelowB2LFloor(latestSelectedOpportunity) &&
                  <div className={classes.warningContainer}>
                    <WarningIcon className={classes.warningIcon} />
                    <Typography className={classes.warningText} variant='caption'>
                      {`The bid is ${ToPercentageStringWithDecimals(bidPushedB2LFloor - latestSelectedOpportunity.selectedValuationBidPctOfAskingPrice, 0)} below the ${ToPercentageStringWithDecimals(bidPushedB2LFloor, 0)} threshold.`}
                      </Typography>
                  </div>
                }
                <div className={classes.flexContainer}>
                  <div className={classes.flexGrow}>
                    <Typography variant='subtitle2' >
                      {'Valuation Type: ' + selectedValuation?.type}
                    </Typography>
                    <Typography variant='subtitle2' >
                      {'Underwrite User: ' + latestSelectedOpportunity.selectedValuationUserName}
                    </Typography>
                    <Typography variant='subtitle2'>
                      <span>Bid Price: </span>
                      <span>{bidPriceFormatted + ' '}</span>
                      <span className={classNames(isBelowB2LFloor(latestSelectedOpportunity) && classes.warningTextColor)}>{`(${ToPercentageStringWithDecimals(latestSelectedOpportunity.selectedValuationBidPctOfAskingPrice, 0)} of list)`}</span>
                    </Typography>
                    <Typography variant='subtitle2' >
                      <span>Max Bid Price: </span>
                      <span className={classNames((exceedsMaxBidPrice(latestSelectedOpportunity)) && classes.warningTextColor)}>{maxBidPriceFormatted}</span>
                    </Typography>
                  </div>
                  <div className={classes.flexGrow}>
                    <Tooltip title='Highest & Best Pricing' placement='right'>
                      <FormControlLabel
                        className={classes.flexGrow}
                        control={<Checkbox
                          checked={!!isHighestAndBest}
                          color='primary'
                          onChange={handleCheckboxChange}
                          indeterminate={isHighestAndBest === null}
                        />}
                        label="HBP"
                      />
                    </Tooltip>
                  </div>
                </div>
              </>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmitBid} variant="contained" color="primary" style={{ width: 84 }} disabled={isLoading || !latestSelectedOpportunity}>
          {submitIsLoading
            ? <CircularProgress size={24} color='inherit' />
            : hasExistingBid ? 'Update' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const styles = theme => ({
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingTop: 8
  },
  flexGrow: {
    flexGrow: 1
  },
  warningContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  warningText: {
    marginLeft: 4,
    marginTop: 3
  },
  warningTextColor: {
    color: theme.palette.warning.text
  },
  warningIcon: {
    fill: theme.palette.warning.text
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
});

const mapStateToProps = (state) => {
  return {
    user: state.appState.user,
    selectedValuation: state.propertyUWState.selectedValuation,
    selectedOpportunity: state.propertyUWState.propertyUWClientTxnOpp,
    bidPushedB2LFloor: state.propertyUWState.selectedContainer?.bidPushedB2LFloor
  }
};

const mapDispatchToProps = {
  updateBidInPrism,
  appNotify
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(SubmitBidModal);